import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DisciplineCycleDto } from 'shared';

import { apiClient } from '../../lib/api/client';

export const disciplineCycleKeys = {
  all: ['discipline-cycle'] as const,
  current: (accountId: string) => [...disciplineCycleKeys.all, accountId] as const,
};

async function fetchCurrentCycle(accountId: string): Promise<DisciplineCycleDto | null> {
  const response = await apiClient.get<{ cycle: DisciplineCycleDto | null }>('/discipline-cycle', {
    params: { accountId },
  });
  return response.data.cycle;
}

async function resetCycle(input: {
  accountId: string;
  snapshotId: string;
}): Promise<DisciplineCycleDto> {
  const response = await apiClient.post<{ cycle: DisciplineCycleDto }>(
    '/discipline-cycle/reset',
    input,
  );
  return response.data.cycle;
}

export function useDisciplineCycle(accountId: string | null) {
  return useQuery({
    queryKey: accountId
      ? disciplineCycleKeys.current(accountId)
      : [...disciplineCycleKeys.all, 'none'],
    queryFn: () => fetchCurrentCycle(accountId ?? ''),
    enabled: accountId !== null,
    staleTime: 30_000,
  });
}

export function useResetDisciplineCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetCycle,
    onSuccess: async (cycle) => {
      await queryClient.invalidateQueries({
        queryKey: disciplineCycleKeys.current(cycle.accountId),
      });
    },
  });
}
